/**
 * Builds the FormBuilder field config for the Host (Zoom) field.
 *
 * Replaces the generic `useAccountUsersFieldConfig` for `host_id` so the
 * options reflect the legacy `SchZoomHost` constraints:
 *   - Only picked interviewers + the current user can host (the recruiter
 *     scheduling the interview). Arbitrary account users are NOT eligible.
 *   - Each option carries the user's Zoom-hosting status (paid / free /
 *     needs licence / no zoom / rate-limited) so the chip + dropdown row
 *     paint the right tag and disable un-hostable rows.
 *   - When the wizard mounts via edit and the saved host is no longer in
 *     the picked-interviewer list, fall back to the `user_zoom_status`
 *     payload that `_ats_interview.json.jbuilder` emits so the chip still
 *     renders correctly without a fetch-by-id round-trip.
 *   - When the field is disabled by mode (self-schedule + 1:1 with multiple
 *     interviewers — see `legacy SchZoomHost.tsx`), the schema attaches a
 *     hover tooltip explaining why.
 *
 * Pure builder — no React hooks. Caller (`useDetailsFormSchema`) feeds in
 * the current state of interviewer selection, scheduler info, and edit
 * fallbacks, and uses the returned config inside the FormBuilder schema.
 */
import {
  type HostZoomStatus,
  isHostOptionDisabled,
  makeHostDisabledTooltipWrapper,
  renderHostOption,
  renderHostTriggerValue,
} from '../fields/HostRenders';
import { resolveLockedHostId } from '../helpers/resolveLockedHostId';

export interface ScheduleHostFieldOption {
  value: string;
  label: string;
  description?: string;
  image?: string;
  /** When defined, drives the per-option Paid/Free/Busy tag + the
   *  `isOptionDisabled` rule. Undefined ⇒ no chip, never disabled. */
  zoomStatus?: HostZoomStatus;
}

export interface BuildHostFieldArgs {
  /** Picked interviewers (from `participant_default_options` / option cache). */
  participantOptions: ScheduleHostFieldOption[];
  /** The recruiter scheduling the interview (or, in edit mode, whoever
   *  originally created the interview). Optional — adhoc with no
   *  current-user fallback simply hides the entry. */
  currentUser?: {
    id: number | string;
    label: string;
    image?: string;
    /** Resolved from `interviewPlan.user_zoom_status` (create from plan)
     *  or `interview.user_zoom_status` (edit). Undefined in pure-adhoc
     *  flows where no plan response is available. */
    zoomStatus?: HostZoomStatus;
  };
  /** Edit-mode safety net: when the saved host is no longer in the picked
   *  interviewer list AND isn't the current user, we still need a chip
   *  for the Select to render. Caller passes `interview.host` here when
   *  available. */
  savedHost?: ScheduleHostFieldOption;
  /** The form's CURRENT (live) `host_id`, seeded to the saved value on
   *  edit-mount. `savedHost` is added to the option list ONLY when it
   *  equals this — legacy `SchZoomHost` parity: the saved host is surfaced
   *  solely so the Select can render it WHEN IT IS the selection, never as
   *  an extra selectable row otherwise. The BE `host` block falls back to
   *  the interview creator (`ats_interview.host || ats_interview.user`)
   *  with no FE-visible discriminator, so an unconditional `savedHost`
   *  option leaked a non-interviewer into the dropdown.
   *
   *  SCH-71: gating on the LIVE value (not the stable saved id) means a
   *  saved host that is no longer a current interviewer / the current user
   *  is dropped the moment `host_id` is cleared. A valid selected host is
   *  always rendered via its own interviewer / current-user option, so it
   *  never depends on this row — only an invalid (removed-from-roster) host
   *  does, and Zoom requires the host to be the current user or a current
   *  interviewer, so it must not stay re-selectable. */
  currentHostId?: string;
  /** When `true`, the field is locked. Caller sets this for the
   *  self-schedule + one_on_one + multi-interviewer scenario where the
   *  host is decided server-side after the candidate picks a slot. */
  disabled?: boolean;
  /** Hover-tooltip copy when `disabled === true`. */
  disabledTooltip?: string;
  /** Tooltip on the field LABEL — info-icon copy regardless of state. */
  labelTooltip?: string;
  /** Conditionally mounted; defaults to `location === 'zoom'`. Caller can
   *  override for edge cases like custom plan locations. */
  condition?: Record<string, unknown>;
  /** Override the required-field error string. Defaults to "Host is required". */
  requiredMessage?: string;
}

const DEFAULT_LABEL_TOOLTIP =
  "To host, you need to be part of your organization's Zoom account. " +
  'Only the person scheduling the interview or an interviewer can host.';

const DEFAULT_DISABLED_TOOLTIP =
  "For one-on-one interviews with multiple interviewers, the host can't be " +
  'changed because the interviewer is not assigned until the candidate picks ' +
  'a time slot.';

const DEFAULT_REQUIRED_MESSAGE = 'Host is required';

const DEFAULT_NO_OPTIONS_MESSAGE =
  'No valid host found. Add valid participants or choose some other ' +
  'alternative to Zoom.';

/**
 * Returns the FormBuilder schema entry for the Host field. The result is
 * shaped to drop into `useDetailsFormSchema`'s schema array.
 */
export function buildScheduleHostField(args: BuildHostFieldArgs) {
  const {
    participantOptions,
    currentUser,
    savedHost,
    currentHostId,
    disabled = false,
    disabledTooltip = DEFAULT_DISABLED_TOOLTIP,
    labelTooltip = DEFAULT_LABEL_TOOLTIP,
    condition,
    requiredMessage = DEFAULT_REQUIRED_MESSAGE,
  } = args;

  // Build the option list:
  //   1. Every picked interviewer (legacy: only attendees can host).
  //   2. + the current user, if not already in the interviewer list.
  //   3. + the saved host (edit mode), if it's neither an interviewer nor
  //      the current user. Without this, the Select would render blank
  //      when the recruiter swaps interviewers post-save.
  // Each option carries `disabled` computed from `zoomStatus` — kula-ai
  // Select reads per-option `disabled` directly and prevents picking
  // (legacy `SchZoomHost` did this via react-select's `isOptionDisabled`
  // callback, which the kula-ai Select doesn't expose).
  const seen = new Set<string>();
  const options: Array<ScheduleHostFieldOption & { disabled?: boolean }> = [];

  const withDisabled = (
    o: ScheduleHostFieldOption
  ): ScheduleHostFieldOption & { disabled?: boolean } => ({
    ...o,
    disabled: isHostOptionDisabled(o.zoomStatus),
  });

  for (const opt of participantOptions) {
    if (!opt.value || seen.has(opt.value)) continue;
    seen.add(opt.value);
    options.push(withDisabled(opt));
  }

  if (currentUser) {
    const id = String(currentUser.id);
    if (id && !seen.has(id)) {
      seen.add(id);
      options.push(
        withDisabled({
          value: id,
          label: currentUser.label,
          image: currentUser.image,
          zoomStatus: currentUser.zoomStatus,
        })
      );
    }
  }

  // Legacy `SchZoomHost` parity: only surface `savedHost` when it IS the
  // current (live) selection — so the Select can render the chosen value,
  // incl. the brief window before SCH-71 clears a host that was removed
  // from the roster. When it isn't the current selection it must NOT appear
  // as a selectable row; otherwise the BE creator-fallback
  // (`ats_interview.host || ats_interview.user`) — or a saved host since
  // removed from the interviewer list — leaks a non-interviewer into the
  // dropdown, where it could be re-picked and 404 every slot's Zoom meeting
  // (host must be the current user or a current interviewer). A valid
  // selected host is always rendered via its own interviewer / current-user
  // option, so dropping this row never blanks a legitimate chip.
  const savedHostGatePassed = !!(
    savedHost?.value &&
    !seen.has(savedHost.value) &&
    String(savedHost.value) === String(currentHostId ?? '')
  );
  if (savedHostGatePassed) {
    seen.add(savedHost!.value);
    options.push(withDisabled(savedHost!));
  }

  // When locked (self-schedule one_on_one + multi-interviewer) the value
  // is resolved — organizer first, else first valid interviewer (see
  // `resolveLockedHostId`). Set it as `defaultValue` so FormBuilder's own
  // conditional-field reset (Host is gated on `location==='zoom'`;
  // `preserveValue` defaults false → on re-show it resets to
  // `defaultValue`) lands on the right host instead of empty. The
  // dedicated lock effect in `DetailsStep` covers the dynamic transition
  // (interviewer count crossing >1 while the field stays mounted).
  const lockedDefault = disabled
    ? resolveLockedHostId({
        organizer: currentUser
          ? { id: currentUser.id, zoomStatus: currentUser.zoomStatus }
          : undefined,
        participantOptions,
      }) || undefined
    : undefined;
  return {
    type: 'select' as const,
    name: 'host_id',
    label: 'Host',
    placeholder: 'Select host',
    required: requiredMessage,
    disabled,
    ...(lockedDefault ? { defaultValue: lockedDefault } : {}),
    options,
    tooltip: labelTooltip,
    // Locked-state hover tooltip. kula-ai `Select` has no `tooltip` prop,
    // so this rides on FormBuilder's field-level `wrapper` (≥ 1.10.82)
    // instead — wraps the whole rendered block in a kula-ai Tooltip,
    // matching legacy `SchZoomHost`.
    ...(disabled && disabledTooltip
      ? { wrapper: makeHostDisabledTooltipWrapper(disabledTooltip) }
      : {}),
    ...(condition ? { condition } : {}),
    fieldProps: {
      searchable: true,
      mode: 'single' as const,
      isClearable: false,
      renderOption: renderHostOption,
      renderTriggerValue: renderHostTriggerValue,
      noOptionsMessage: DEFAULT_NO_OPTIONS_MESSAGE,
    },
  };
}
